import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GlobalApiService } from '../../../shared-modules/global-api.service';
import { AuthService } from '../../../shared-modules/services/auth.service';

/**
 * Lead Dashboard API (backend: Controllers/LeadsGym.Controller.js, docs/plan-find-centre-leads.md P6).
 * Admin only. Every call resolves to { ok, code, message, data } so the page never sees raw errors:
 *   code '001' ok | '007' no credits | '008' lead no longer available | '013' converted by another centre (confirm) | '404' feature not live yet | 'error'
 */
export interface LeadApiResult<T = any> { ok: boolean; code: string; message: string; data?: T; }

export interface LeadOffer {
  offerId: string; status: string; branchId: string; branchName: string; category: string; area: string;
  distanceKm: number | null; offeredAt: string; expiresAt: string | null; minutesLeft: number | null;   // null = no time limit
  customer: { name: string; phoneMasked: string };
  free?: boolean;                 // a running campaign makes this accept free (live)
  converted?: boolean;            // another centre converted this lead - accepting asks to confirm (still charged)
}
export interface AcceptedLead { leadId: string; name: string; phoneNo: string; category: string; area: string; branchId: string; acceptedAt: string; }
export interface LeadStatementRow { date: string; change: number; balanceAfter: number; type: string; leadId?: string; campaignId?: string; }
export interface LeadCampaignBanner { name: string; freeTotal: number; endsAt: string | null; cities: string[]; freeLeftByCentre: { [centreId: string]: number }; }
export interface LeadBalance { centres: { centreId: string; centreName: string; balance: number; statement: LeadStatementRow[] }[]; pricePerLead: number; gstPercent: number; campaign: LeadCampaignBanner | null; }

export interface CreditQuote { centreId: string; credits: number; unitPrice: number; taxable: number; cgst: number; sgst: number; igst: number; gst: number; total: number; placeOfSupply: { state: string | null }; }
export interface CreditOrder { purchaseId: string; orderId: string; amountPaise: number; key: string; price: CreditQuote; }
export interface CreditPurchase { purchaseId: string; credits: number; total: number; paidAt: string; invoiceNo?: string; invoiceUrl?: string; }

export const REJECT_REASONS: { value: string; label: string }[] = [
  { value: 'too_far', label: 'Too far' },
  { value: 'wrong_category', label: 'We don\'t offer this' },
  { value: 'batch_full', label: 'Batch full' },
  { value: 'price_mismatch', label: 'Price mismatch' },
  { value: 'already_our_customer', label: 'Already our member' },
  { value: 'other', label: 'Other' },
];

@Injectable({ providedIn: 'root' })
export class LeadDashboardService {
  constructor(private http: HttpClient, private globalApiService: GlobalApiService, private authService: AuthService) { }

  private post<T>(path: string, body: any = {}): Observable<LeadApiResult<T>> {
    return this.http.post<any>(this.globalApiService.getApiUrl() + path, body).pipe(
      map((r: any): LeadApiResult<T> => ({ ok: !!r && r.statusCode === '001', code: (r && r.statusCode) || 'error', message: (r && r.Message) || '', data: r && r.data })),
      catchError((e: any): Observable<LeadApiResult<T>> => {
        if (e && e.status === 401) { this.authService.getCheckError(e); return throwError(e); }
        if (e && e.status === 404) { return of({ ok: false, code: '404', message: 'Leads are not live yet.' }); }
        const msg = (e && e.error && e.error.Message) || 'Something went wrong. Please try again.';
        return of({ ok: false, code: (e && e.error && e.error.statusCode) || 'error', message: msg });
      })
    );
  }

  offers(): Observable<LeadApiResult<LeadOffer[]>> { return this.post<LeadOffer[]>('/leads/offers'); }
  // confirmConverted: the admin agreed to accept a lead another centre converted (code 013 otherwise)
  accept(offerId: string, confirmConverted = false): Observable<LeadApiResult<any>> { return this.post('/leads/offers/accept', { offerId, confirmConverted }); }
  reject(offerId: string, reason: string, note?: string): Observable<LeadApiResult<any>> { return this.post('/leads/offers/reject', { offerId, reason, note }); }
  myLeads(): Observable<LeadApiResult<AcceptedLead[]>> { return this.post<AcceptedLead[]>('/leads/mine'); }
  balance(): Observable<LeadApiResult<LeadBalance>> { return this.post<LeadBalance>('/leads/balance'); }

  // buying credits (P7) - every amount comes from the server
  quote(credits: number): Observable<LeadApiResult<CreditQuote>> { return this.post<CreditQuote>('/leads/credits/quote', { credits }); }
  order(credits: number, offerId?: string, confirmConverted = false): Observable<LeadApiResult<CreditOrder>> { return this.post<CreditOrder>('/leads/credits/order', { credits, offerId, confirmConverted }); }
  verify(r: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }): Observable<LeadApiResult<any>> { return this.post('/leads/credits/verify', r); }
  failed(orderId: string): Observable<LeadApiResult<any>> { return this.post('/leads/credits/failed', { orderId }); }
  purchases(): Observable<LeadApiResult<CreditPurchase[]>> { return this.post<CreditPurchase[]>('/leads/credits/purchases'); }
}
